import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const HANDLING_OPTIONS = [
  { key: "fragile", label: "Fragile items" },
  { key: "high_value", label: "High value shipments (>₹50,000)" },
  { key: "bulk_ftl", label: "Bulk / FTL" },
  { key: "cold_chain", label: "Cold chain / Perishables" },
  { key: "documents_only", label: "Documents only" },
];

export const validateStep4 = () => ({}); // all optional

export default function Step4Operations({ formData, setFormData }) {
  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  const toggleHandling = (key) => {
    const current = formData.willing_to_handle || [];
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    set("willing_to_handle", next);
  };

  const hasWarehouse = formData.has_warehouse === true;

  return (
    <div className="space-y-7" data-testid="step4-operations">
      <div>
        <h2 className="text-lg font-bold text-slate-800">Operations Profile</h2>
        <p className="text-sm text-slate-500">
          Help us understand the partner&apos;s capacity (all optional)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Number of Employees" testid="employees">
          <Input
            type="number"
            min="0"
            value={formData.num_employees ?? ""}
            onChange={(e) =>
              set(
                "num_employees",
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            placeholder="10"
            data-testid="employees-input"
          />
        </Field>

        <Field
          label="Avg Monthly Shipments Handled"
          helper="Approximate volume across all clients"
          testid="monthly-shipments"
        >
          <Input
            type="number"
            min="0"
            value={formData.avg_monthly_shipments ?? ""}
            onChange={(e) =>
              set(
                "avg_monthly_shipments",
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            placeholder="500"
            data-testid="monthly-shipments-input"
          />
        </Field>

        <div className="md:col-span-2">
          <Field label="Current Clients / Tie-ups" testid="clients">
            <Textarea
              value={formData.current_clients || ""}
              onChange={(e) => set("current_clients", e.target.value)}
              placeholder="e.g. Amazon, Flipkart, local shops"
              rows={3}
              data-testid="clients-input"
            />
          </Field>
        </div>

        <Field label="Operating Hours" testid="hours">
          <Input
            value={formData.operating_hours || ""}
            onChange={(e) => set("operating_hours", e.target.value)}
            placeholder="e.g. 9 AM – 7 PM, Mon–Sat"
            data-testid="hours-input"
          />
        </Field>

        <Field label="Warehouse / Office Space Available" testid="warehouse">
          <div className="flex gap-2">
            {[
              { v: true, l: "Yes" },
              { v: false, l: "No" },
            ].map(({ v, l }) => (
              <button
                key={String(v)}
                type="button"
                onClick={() => set("has_warehouse", v)}
                className={`px-4 py-1.5 rounded-md border text-sm font-medium transition ${
                  formData.has_warehouse === v
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                }`}
                data-testid={`warehouse-${l.toLowerCase()}`}
              >
                {l}
              </button>
            ))}
          </div>
        </Field>

        {hasWarehouse && (
          <Field label="Space Size (sqft)" testid="warehouse-sqft">
            <Input
              type="number"
              min="0"
              value={formData.warehouse_sqft ?? ""}
              onChange={(e) =>
                set(
                  "warehouse_sqft",
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              placeholder="1500"
              data-testid="warehouse-sqft-input"
            />
          </Field>
        )}
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
          Willing to handle
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {HANDLING_OPTIONS.map((opt) => {
            const checked = (formData.willing_to_handle || []).includes(opt.key);
            return (
              <label
                key={opt.key}
                className={`flex items-center gap-2.5 border rounded-md px-3 py-2 cursor-pointer text-sm transition ${
                  checked
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid={`handle-${opt.key}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleHandling(opt.key)}
                  className="rounded text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                />
                <span className="text-slate-700">{opt.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Field({ label, helper, error, children, testid }) {
  return (
    <div data-testid={`field-${testid}`}>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {helper && !error && (
        <p className="text-[11px] text-slate-400 mt-1">{helper}</p>
      )}
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}
